<script setup lang="ts">
import type {
  PlayerParty,
  PlayerSocialFriend,
  PlayerSocialFriendRequest,
} from '@/api/public'
import PlayerAvatar from '@/components/PlayerAvatar.vue'
import {
  playerPartyInviteAvailability,
  playerPartyInviteUnavailableReason,
  playerSocialStatusNeedsClock,
  playerSocialStatusPresentation,
  type PlayerPartyInviteAvailability,
  type PlayerSocialStatusPresentation,
} from '@/lib/playerSocialPresentation'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'

const props = defineProps<{
  friends: PlayerSocialFriend[]
  outgoingRequests: PlayerSocialFriendRequest[]
  party: PlayerParty | null
  currentSteamId: string
  partyInviteCooldowns: Record<string, number>
  partyInviteLocked: boolean
  partyInviteLockReason: string
  loading: boolean
  actionPending: boolean
  error: string | null
}>()

const emit = defineEmits<{
  close: []
  addFriend: [trigger: HTMLElement | null]
  openProfile: [steamID: string]
  message: [steamID: string, trigger: HTMLElement | null]
  invite: [steamID: string]
  removeRequest: [requestID: string]
  clearError: []
}>()

const statusClock = ref(Date.now())
let statusClockTimer: ReturnType<typeof setInterval> | null = null
const relativeFriendStatusActive = computed(() => props.friends.some(friend =>
  playerSocialStatusNeedsClock(
    friend.presence,
    friend.game_activity,
    statusClock.value,
  ),
))
const friendStatuses = computed(() => {
  const now = new Date(statusClock.value)
  return new Map(props.friends.map(friend => [
    friend.id,
    playerSocialStatusPresentation(friend.presence, friend.game_activity, now),
  ]))
})

watch(relativeFriendStatusActive, active => {
  if (active && statusClockTimer === null) {
    statusClockTimer = setInterval(() => {
      statusClock.value = Date.now()
    }, 60_000)
  } else if (!active && statusClockTimer !== null) {
    clearInterval(statusClockTimer)
    statusClockTimer = null
  }
}, { immediate: true })

onBeforeUnmount(() => {
  if (statusClockTimer !== null) clearInterval(statusClockTimer)
})

function friendName(friend: PlayerSocialFriend): string {
  return friend.player.persona_name || friend.player.steam_id
}

function friendStatus(friend: PlayerSocialFriend): PlayerSocialStatusPresentation {
  return friendStatuses.value.get(friend.id) ??
    playerSocialStatusPresentation(friend.presence, friend.game_activity)
}

function requestName(request: PlayerSocialFriendRequest): string {
  return request.player.persona_name || request.player.steam_id
}

function inviteAvailability(friend: PlayerSocialFriend): PlayerPartyInviteAvailability {
  return playerPartyInviteAvailability(
    props.party,
    props.currentSteamId,
    {
      targetSteamID: friend.player.steam_id,
      rosterLocked: props.partyInviteLocked,
      actionPending: props.actionPending,
      cooldownSeconds: props.partyInviteCooldowns[friend.player.steam_id] ?? 0,
    },
  )
}

function canInvite(friend: PlayerSocialFriend): boolean {
  return inviteAvailability(friend).allowed
}

function inviteLabel(friend: PlayerSocialFriend): string {
  return playerPartyInviteUnavailableReason(
    inviteAvailability(friend),
    props.partyInviteLockReason,
  ) || 'Пригласить в группу'
}

function requestPartyInvite(friend: PlayerSocialFriend): void {
  if (canInvite(friend)) emit('invite', friend.player.steam_id)
}

function requestProfile(friend: PlayerSocialFriend): void {
  emit('openProfile', friend.player.steam_id)
}

function requestDirectMessage(friend: PlayerSocialFriend, event: MouseEvent): void {
  emit(
    'message',
    friend.player.steam_id,
    event.currentTarget instanceof HTMLElement ? event.currentTarget : null,
  )
}

function requestAddFriend(event: MouseEvent): void {
  emit(
    'addFriend',
    event.currentTarget instanceof HTMLElement ? event.currentTarget : null,
  )
}
</script>

<template>
  <section
    id="player-friends-panel"
    class="player-dock-panel friends-panel"
    role="dialog"
    aria-modal="true"
    aria-labelledby="player-friends-panel-title"
    tabindex="-1"
  >
    <header class="player-dock-panel-header">
      <div>
        <h2 id="player-friends-panel-title">Друзья</h2>
      </div>
      <div class="player-dock-panel-header-actions">
        <button
          class="friend-add-action"
          type="button"
          aria-label="Добавить друга"
          title="Добавить друга"
          :disabled="actionPending"
          @click="requestAddFriend"
        >
          <img src="/shared/assets/addfriend_png.png" alt="" aria-hidden="true" />
        </button>
        <button class="player-dock-close" type="button" aria-label="Закрыть друзей" @click="$emit('close')">
          <img src="/shared/assets/x_close_png.png" alt="" aria-hidden="true" />
        </button>
      </div>
    </header>

    <div v-if="error" class="player-dock-alert" role="alert">
      <span>{{ error }}</span>
      <button type="button" aria-label="Скрыть ошибку" @click="$emit('clearError')">×</button>
    </div>

    <div v-if="loading && friends.length === 0" class="player-dock-empty compact" role="status">
      Загружаем друзей…
    </div>

    <section v-else class="friend-list-section" aria-labelledby="all-friends-title">
      <h3 id="all-friends-title">
        Все друзья <b>{{ friends.length }}</b>
      </h3>
      <p v-if="friends.length === 0" class="friend-list-empty">
        Список друзей пока пуст.
      </p>
      <div v-else class="friend-list">
        <article
          v-for="friend in friends"
          :key="friend.id"
          class="friend-row"
          :title="friendStatus(friend).title"
        >
          <button
            class="friend-profile-action"
            type="button"
            :aria-label="`Открыть профиль ${friendName(friend)}`"
            @click="requestProfile(friend)"
          >
            <PlayerAvatar
              :name="friendName(friend)"
              :avatar-url="friend.player.avatar_url"
              size="sm"
              decorative
            />
            <span class="friend-copy">
              <strong>{{ friendName(friend) }}</strong>
            </span>
          </button>
          <RouterLink
            v-if="friendStatus(friend).liveID"
            class="friend-player-status"
            :class="`is-${friendStatus(friend).kind}`"
            :to="{
              name: 'live-match-detail',
              params: { live_id: friendStatus(friend).liveID },
            }"
            :aria-label="`${friendStatus(friend).title} ${friendName(friend)}`"
            :title="friendStatus(friend).title"
            @click.stop="$emit('close')"
          >
            <span class="friend-player-status-dot" aria-hidden="true"></span>
            <span>{{ friendStatus(friend).label }}</span>
          </RouterLink>
          <span
            v-else
            class="friend-player-status"
            :class="`is-${friendStatus(friend).kind}`"
            :title="friendStatus(friend).title"
          >
            <span class="friend-player-status-dot" aria-hidden="true"></span>
            <span>{{ friendStatus(friend).label }}</span>
          </span>
          <button
            class="friend-message-action"
            type="button"
            :aria-label="`Написать ${friendName(friend)}`"
            title="Написать сообщение"
            @click.stop="requestDirectMessage(friend, $event)"
          >
            <img
              src="/shared/assets/icon_send_message_psd.png"
              alt=""
              aria-hidden="true"
            />
          </button>
          <button
            class="friend-party-invite"
            type="button"
            :disabled="!canInvite(friend)"
            :aria-disabled="!canInvite(friend)"
            :aria-label="`${inviteLabel(friend)}: ${friendName(friend)}`"
            :title="inviteLabel(friend)"
            @click.stop="requestPartyInvite(friend)"
          >
            <img src="/shared/assets/plus_png.png" alt="" aria-hidden="true" />
          </button>
        </article>
      </div>
    </section>

    <section
      v-if="outgoingRequests.length"
      class="friend-outgoing-section"
      aria-labelledby="outgoing-friends-title"
    >
      <h3 id="outgoing-friends-title">
        Отправленные заявки <b>{{ outgoingRequests.length }}</b>
      </h3>
      <ul class="friend-outgoing-list">
        <li v-for="request in outgoingRequests" :key="request.id" class="friend-outgoing-row">
          <PlayerAvatar
            :name="requestName(request)"
            :avatar-url="request.player.avatar_url"
            size="sm"
            decorative
          />
          <div class="friend-copy">
            <strong>{{ requestName(request) }}</strong>
            <span>Заявка отправлена</span>
          </div>
          <button
            type="button"
            :aria-label="`Отменить заявку в друзья для ${requestName(request)}`"
            :disabled="actionPending"
            @click="$emit('removeRequest', request.id)"
          >
            Отменить
          </button>
        </li>
      </ul>
    </section>
  </section>
</template>
